'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Header } from '@/components/layout/Header'
import { Sidebar, useSidebar } from '@/components/layout/Sidebar'
import { MobileSidebar } from '@/components/layout/MobileSidebar'
import { VisualPattern } from '@/components/brand'
import { cn } from '@/lib/utils'

const AUTH_ROUTES = ['/auth/signin', '/auth/register', '/auth/error']

export function LayoutContent({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isCollapsed } = useSidebar()
  const pathname = usePathname()

  const isAuthRoute = AUTH_ROUTES.some((route) => pathname?.startsWith(route))

  if (isAuthRoute) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100'>
        <VisualPattern variant='subtle' />
        <main className='relative z-10 flex min-h-screen items-center justify-center p-4'>
          {children}
        </main>
      </div>
    )
  }

  return (
    <>
      <VisualPattern variant='subtle' />
      <Sidebar />
      <MobileSidebar
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
      <div
        className={cn(
          'min-h-screen',
          isCollapsed ? 'md:pl-16' : 'md:pl-64',
          'transition-all duration-300 ease-out'
        )}
      >
        <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />
        <main
          id='main-content'
          role='main'
          aria-label='Main content'
          className={cn(
            'flex flex-col',
            'pt-24',
            'px-4 pb-8 lg:px-8',
            'min-h-screen'
          )}
          tabIndex={-1}
        >
          {children}
        </main>
      </div>
    </>
  )
}
